// File: frontend/src/pages/SuperAdmin/components/CommunicationManagement/index.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../contexts/LanguageContext';
import EmailTemplates from './EmailTemplates';
import SmsTemplates from './SmsTemplates';
import CommunicationLogs from './CommunicationLogs';
import styles from './CommunicationManagement.module.css';

const CommunicationManagement = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState('email');
  const [templateId, setTemplateId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  
  useEffect(() => {
    const path = location.pathname;
    
    if (path.includes('/communications/email')) {
      setView('email');
      
      if (path.includes('/create')) {
        setIsCreateMode(true);
        setIsEditMode(false);
        setTemplateId(null);
      } else if (path.match(/\/email\/[^/]+\/edit$/)) {
        setIsEditMode(true);
        setIsCreateMode(false);
        const id = path.split('/').slice(-2)[0];
        setTemplateId(id);
      } else if (path.match(/\/email\/[^/]+$/)) {
        setIsEditMode(false);
        setIsCreateMode(false);
        const id = path.split('/').pop();
        setTemplateId(id);
      } else {
        setIsEditMode(false);
        setIsCreateMode(false);
        setTemplateId(null);
      }
    } else if (path.includes('/communications/sms')) {
      setView('sms');
      
      if (path.includes('/create')) {
        setIsCreateMode(true);
        setIsEditMode(false);
        setTemplateId(null);
      } else if (path.match(/\/sms\/[^/]+\/edit$/)) {
        setIsEditMode(true);
        setIsCreateMode(false);
        const id = path.split('/').slice(-2)[0];
        setTemplateId(id);
      } else if (path.match(/\/sms\/[^/]+$/)) {
        setIsEditMode(false);
        setIsCreateMode(false);
        const id = path.split('/').pop();
        setTemplateId(id);
      } else {
        setIsEditMode(false);
        setIsCreateMode(false);
        setTemplateId(null);
      }
    } else if (path.includes('/communications/logs')) {
      setView('logs');
      setIsEditMode(false);
      setIsCreateMode(false);
      setTemplateId(null);
    } else {
      // Default to email templates
      setView('email');
      setIsEditMode(false);
      setIsCreateMode(false);
      setTemplateId(null);
    }
  }, [location.pathname]);
  
  const handleViewEmail = () => {
    navigate('/super-admin-panel/communications/email');
  };
  
  const handleViewSms = () => {
    navigate('/super-admin-panel/communications/sms');
  };
  
  const handleViewLogs = () => {
    navigate('/super-admin-panel/communications/logs');
  };
  
  const handleViewTemplate = (type, id) => {
    navigate(`/super-admin-panel/communications/${type}/${id}`);
  };
  
  const handleEditTemplate = (type, id) => {
    navigate(`/super-admin-panel/communications/${type}/${id}/edit`);
  };
  
  const handleCreateTemplate = (type) => {
    navigate(`/super-admin-panel/communications/${type}/create`);
  };
  
  const handleBackToList = (type) => {
    navigate(`/super-admin-panel/communications/${type}`);
  };
  
  // Wrap the navigation functions into a single handler for child components
  const handleViewChange = (newView, id = null, mode = 'view') => {
    if (newView === 'email' || newView === 'sms') {
      if (mode === 'view' && id) {
        handleViewTemplate(newView, id);
      } else if (mode === 'edit' && id) {
        handleEditTemplate(newView, id);
      } else if (mode === 'create') {
        handleCreateTemplate(newView);
      } else {
        handleBackToList(newView);
      }
    } else if (newView === 'logs') {
      handleViewLogs();
    }
  };

  // Render the appropriate view
  const renderView = () => {
    switch (view) {
      case 'email':
        return (
          <EmailTemplates 
            templateId={templateId}
            isEditMode={isEditMode}
            isCreateMode={isCreateMode}
            onViewChange={handleViewChange}
          />
        );
      case 'sms':
        return (
          <SmsTemplates 
            templateId={templateId}
            isEditMode={isEditMode}
            isCreateMode={isCreateMode}
            onViewChange={handleViewChange}
          />
        );
      case 'logs':
        return (
          <CommunicationLogs 
            onViewChange={handleViewChange}
          />
        );
      default:
        return (
          <EmailTemplates 
            templateId={templateId}
            isEditMode={isEditMode}
            isCreateMode={isCreateMode}
            onViewChange={handleViewChange}
          />
        );
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>
        {t('communication_management')}
      </h1>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${view === 'email' ? styles.activeTab : ''}`}
          onClick={handleViewEmail}
        >
          <i className="fas fa-envelope"></i>
          <span>{t('email_templates')}</span>
        </button>
        <button 
          className={`${styles.tab} ${view === 'sms' ? styles.activeTab : ''}`}
          onClick={handleViewSms}
        >
          <i className="fas fa-sms"></i>
          <span>{t('sms_templates')}</span>
        </button>
        <button 
          className={`${styles.tab} ${view === 'logs' ? styles.activeTab : ''}`}
          onClick={handleViewLogs}
        >
          <i className="fas fa-history"></i>
          <span>{t('communication_logs')}</span>
        </button>
      </div>
      
      <div className={styles.content}>
        {renderView()}
      </div>
    </div>
  );
};

export default CommunicationManagement;